/*
 * GATE 3 GOVERNANCE — OWNER ONLY, read-only, synthetic-only.
 * Reads GET /api/founder/ci-gate3. Shows counts and states only; it can change nothing.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck } from "lucide-react";
import { Panel, Note, Chip, fmt } from "./ConsolePrimitives";
import { CIApproverPanel } from "./CIApproverPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/founder/ci-gate3`;

const CHECK_LABELS = {
  environment_identity_verified: "Environment identity verified",
  production_environment: "Running in Production",
  transactional_handle_ready: "Transactional database handle",
  master_switch_on: "Master switch",
  kill_switch_clear: "Kill switch clear",
  gate3_switch_on: "Gate 3 switch",
  ledger_key_configured: "Ledger key configured",
  approval_verify_keys_configured: "Approval verification keys",
  two_person_approver_keys_configured: "Two-person approver keys",
  audit_anchor_verified: "External audit anchor",
  feed_authorized: "Feed to companion authorized",
  real_data_authorized: "Real-member data authorized",
};

const Readiness = ({ r }) => (
  <Panel title="Activation readiness" icon={ShieldCheck} testId="fc-g3-readiness">
    <Chip tone={r.ready ? "warn" : "quiet"} testId="fc-g3-ready">{r.ready ? "Ready to activate" : "Not ready to activate"}</Chip>
    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
      {Object.entries(r.checks || {}).map(([k, ok]) => (
        <Chip key={k} tone={ok ? "quiet" : "warn"} testId={`fc-g3-check-${k}`}>{CHECK_LABELS[k] || k}: {ok ? "met" : "not met"}</Chip>
      ))}
    </div>
    <Note testId="fc-g3-acceptance">Independent acceptance (Gate 2 holdout, Gate 3 review) is not recorded by this system. Check the governance record.</Note>
  </Panel>
);

const Tally = ({ counts, prefix }) => {
  const rows = Object.entries(counts || {});
  if (!rows.length) return <Note testId={`${prefix}-none`}>None recorded.</Note>;
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
      {rows.map(([k, n]) => (
        <Chip key={k} tone="quiet" testId={`${prefix}-${k}`}>{k}: {fmt(n)}</Chip>
      ))}
    </div>
  );
};

const Switch = ({ on, label, testId }) => (
  <Chip tone={on ? "warn" : "quiet"} testId={testId}>{label}: {on ? "on" : "off"}</Chip>
);

export const CIGate3View = () => {
  const [data, setData] = useState(null);
  const [ready, setReady] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [status, readiness] = await Promise.all([axios.get(API), axios.get(`${API}/readiness`)]);
      setData(status.data);
      setReady(readiness.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.status === 403
        ? "Gate 3 governance is owner-only."
        : "Gate 3 governance could not be read just now.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <header className="fc__section-head">
        <h2 data-testid="fc-g3-title">Gate 3 governance</h2>
        <p data-testid="fc-g3-banner">{data?.banner}</p>
      </header>
      <Panel title="Activation boundaries" icon={ShieldCheck} testId="fc-g3-panel">
        {data && !data.active && <Note testId="fc-g3-inactive">Collective Intelligence is not active in this environment.</Note>}
        {data?.active && !data.available && <Note testId="fc-g3-unavailable">Gate 3 status could not be read. Nothing is assumed.</Note>}
        {data?.available && (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Switch on={data.gate3_enabled} label="Gate 3 switch" testId="fc-g3-enabled" />
              <Switch on={data.feed_authorized} label="feed to companion" testId="fc-g3-feed" />
              <Switch on={data.real_data_authorized} label="real-member data" testId="fc-g3-real" />
              <Chip tone={data.audit_chain_valid ? "quiet" : "warn"} testId="fc-g3-chain">
                audit chain: {data.audit_chain_valid ? "valid" : "broken"}
              </Chip>
              <Chip tone="quiet" testId="fc-g3-anchor">anchor: {String(data.anchor_state?.state ?? data.anchor_state ?? "—")}</Chip>
            </div>
            <p style={{ marginTop: 12 }} data-testid="fc-g3-last-job">
              Last scheduled run: {data.last_job?.at || "never"}{data.last_job?.outcome ? ` · ${data.last_job.outcome}` : ""}
            </p>
            <p style={{ marginTop: 12 }}>Findings by state</p>
            <Tally counts={data.findings_by_state} prefix="fc-g3-finding" />
            <p style={{ marginTop: 12 }}>Window certificates by outcome</p>
            <Tally counts={data.certificates_by_outcome} prefix="fc-g3-cert" />
            <p style={{ marginTop: 12 }}>Releases by state</p>
            <Tally counts={data.releases_by_state} prefix="fc-g3-release" />
            <p style={{ marginTop: 12 }} data-testid="fc-g3-attention">
              Pending two-person approvals: <b>{fmt(data.pending_approvals)}</b> · Expiring within 14 days: <b>{fmt(data.expiring_within_14_days)}</b> · Review due within 14 days: <b>{fmt(data.review_due_within_14_days)}</b>
            </p>
          </>
        )}
        {error && <Note testId="fc-g3-error">{error}</Note>}
      </Panel>
      {ready && <Readiness r={ready} />}
      <CIApproverPanel />
    </>
  );
};
